
import React, { useState, useEffect } from 'react';
import { Box, Typography, TextField, Button, Grid, Paper, Divider, IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { collection, addDoc, getDocs, updateDoc, doc } from 'firebase/firestore';
import { firestore } from '../../../firebase.config';

const AccountingConfig = () => {
  const [banks, setBanks] = useState([]);
  const [bankForm, setBankForm] = useState({ name: '', code: '' });
  const [branchForm, setBranchForm] = useState({ branchName: '', branchCode: '', accountNumber: '', ifsc: '', address: '', contact: '' });
  const [selectedBankId, setSelectedBankId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    // Fetch banks from Firestore
    const fetchBanks = async () => {
      const querySnapshot = await getDocs(collection(firestore, 'AccountingBanks'));
      setBanks(querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    };
    fetchBanks();
  }, [success]);

  const handleBankChange = (e) => {
    setBankForm({ ...bankForm, [e.target.name]: e.target.value });
  };
  const handleBranchChange = (e) => {
    setBranchForm({ ...branchForm, [e.target.name]: e.target.value });
  };

  // Delete branch from a bank
  const handleDeleteBranch = async (bankId, branchIdx) => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const bankDoc = doc(firestore, 'AccountingBanks', bankId);
      const bank = banks.find(b => b.id === bankId);
      const branches = Array.isArray(bank?.branches) ? bank.branches : [];
      const newBranches = branches.filter((_, idx) => idx !== branchIdx);
      await updateDoc(bankDoc, { branches: newBranches });
      setSuccess('Branch deleted successfully!');
    } catch (err) {
      setError('Failed to delete branch.');
    } finally {
      setLoading(false);
    }
  };

  const handleBankSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      await addDoc(collection(firestore, 'AccountingBanks'), { ...bankForm, branches: [] });
      setSuccess('Bank added successfully!');
      setBankForm({ name: '', code: '' });
    } catch (err) {
      setError('Failed to add bank.');
    } finally {
      setLoading(false);
    }
  };

  const handleBranchSubmit = async (e) => {
    e.preventDefault();
    if (!selectedBankId) {
      setError('Select a bank to add branch');
      return;
    }
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const bankDoc = doc(firestore, 'AccountingBanks', selectedBankId);
      // Get current branches
      const bank = banks.find(b => b.id === selectedBankId);
      const branches = Array.isArray(bank?.branches) ? bank.branches : [];
      const newBranches = [...branches, branchForm];
      await updateDoc(bankDoc, { branches: newBranches });
      setSuccess('Branch added successfully!');
      setBranchForm({ branchName: '', branchCode: '', accountNumber: '', ifsc: '', address: '', contact: '' });
    } catch (err) {
      setError('Failed to add branch.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>Bank Configuration</Typography>
      <Paper sx={{ p: 2, mb: 3 }}>
        <form onSubmit={handleBankSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField label="Bank Name" name="name" value={bankForm.name} onChange={handleBankChange} fullWidth required />
            </Grid>
            {/* <Grid item xs={12} sm={6}>
              <TextField label="Bank Code" name="code" value={bankForm.code} onChange={handleBankChange} fullWidth required />
            </Grid> */}
            <Grid item xs={12}>
              <Button type="submit" variant="contained" color="primary" disabled={loading}>Add Bank</Button>
            </Grid>
          </Grid>
        </form>
        <Divider sx={{ my: 3 }} />
        <Typography variant="h6" sx={{ mb: 1 }}>Add Branch to Bank</Typography>
        <form onSubmit={handleBranchSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField select label="Select Bank" name="selectedBankId" value={selectedBankId} onChange={e => setSelectedBankId(e.target.value)} fullWidth SelectProps={{ native: true }} required>
                <option value="">Select Bank</option>
                {banks.map(bank => (
                  <option key={bank.id} value={bank.id}>{bank.name}</option>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Branch Name" name="branchName" value={branchForm.branchName} onChange={handleBranchChange} fullWidth required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Branch Code" name="branchCode" value={branchForm.branchCode} onChange={handleBranchChange} fullWidth required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Account Number" name="accountNumber" value={branchForm.accountNumber} onChange={handleBranchChange} fullWidth required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="IFSC" name="ifsc" value={branchForm.ifsc} onChange={handleBranchChange} fullWidth required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Address" name="address" value={branchForm.address} onChange={handleBranchChange} fullWidth />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Contact" name="contact" value={branchForm.contact} onChange={handleBranchChange} fullWidth />
            </Grid>
            <Grid item xs={12}>
              <Button type="submit" variant="contained" color="secondary" disabled={loading}>Add Branch</Button>
            </Grid>
          </Grid>
        </form>
        {error && <Typography color="error" sx={{ mt: 2 }}>{error}</Typography>}
        {success && <Typography color="success.main" sx={{ mt: 2 }}>{success}</Typography>}
      </Paper>
      <Typography variant="h6" sx={{ mb: 1 }}>Existing Branches</Typography>
      <Paper sx={{ p: 2 }}>
        {banks.length === 0 ? (
          <Typography>No banks added yet.</Typography>
        ) : (
          <Grid container spacing={2}>
            {banks.flatMap(bank => (
              Array.isArray(bank.branches) && bank.branches.length > 0
                ? bank.branches.map((branch, idx) => (
                    <Grid item xs={12} sm={6} md={4} key={bank.id + '-' + idx}>
                      <Paper sx={{ p: 2, position: 'relative' }}>
                        <IconButton
                          aria-label="delete"
                          size="small"
                          sx={{ position: 'absolute', top: 8, right: 8 }}
                          onClick={() => handleDeleteBranch(bank.id, idx)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>{bank.name}</Typography>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Branch: {branch.branchName}</Typography>
                        <Typography variant="body2">Branch Code: {branch.branchCode}</Typography>
                        <Typography variant="body2">Account Number: {branch.accountNumber}</Typography>
                        <Typography variant="body2">IFSC: {branch.ifsc}</Typography>
                        <Typography variant="body2">Address: {branch.address}</Typography>
                        <Typography variant="body2">Contact: {branch.contact}</Typography>
                      </Paper>
                    </Grid>
                  ))
                : [
                    <Grid item xs={12} sm={6} md={4} key={bank.id + '-nobranch'}>
                      <Paper sx={{ p: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>{bank.name}</Typography>
                        <Typography variant="body2">No branches added.</Typography>
                      </Paper>
                    </Grid>
                  ]
            ))}
          </Grid>
        )}
      </Paper>
    </Box>
  );
};

export default AccountingConfig;
